import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  FormFields,
  PermissionButton,
  getDefaultValuesForForm,
  PageSection,
  StackablePageButtons,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import schema from './create.json';
import validationSchema from './validations';
import PasswordInstructions from './PasswordInstructions';
import {
  useServiceProvider,
  withServiceProvider,
} from '../../../../../providers/ServiceProviderProvider';
import ServiceProviderLayout from '../../../components/ServiceProviderLayout';
import getSPLevelStackableButtons from '../../../utils/getSPLevelStackableButtons';
import { useServiceModule } from '../../../../../providers/ServiceModuleProvider';

const FORM_ID = 'ServiceProviderUserCreateForm';

function UserCreate() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const store = useStore();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl } = useDataProvider(provider);
  const postApiUrl = `${getApiUrl()}/sxcl/v1/users`;
  const yupValidationSchema = validationSchema(formatMessage);
  const { moduleSettings } = useServiceModule();
  const { serviceProviderId } = useServiceProvider();

  const getEditUrl = newId => {
    return `/serviceproviders/edit/${serviceProviderId}/users/${newId}`;
  };

  const preProcessRequestData = data => {
    const { enabled, insightPurchased, role, ...other } = data;
    return {
      enabled: enabled === 'true',
      companyId: serviceProviderId,
      userRoleId: role.value,
      ...other,
    };
  };

  const onSubmit = async data => {
    const requestData = preProcessRequestData(data);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'USER_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'User was saved successfully',
        }),
      );
      return {
        nextValues: {
          ...data,
          insightPurchased: moduleSettings.insightModulePurchased,
        },
        errors: null,
        callback: () => navigate(getEditUrl(response?.data?.id)),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    const result = {
      data: {
        ...getDefaultValuesForForm({
          schema,
        }),
        smsOptedIn: false,
        insightPurchased: moduleSettings.insightPurchased || false,
      },
      errors: null,
    };
    return result;
  }, []);

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'LIST_USERS',
        defaultMessage: 'List Users',
      }),
      Icon: IconLibrary.User,
      permissionName: 'CAN_LIST_SP_USER',
      variant: 'outlined',
      onClick: () => {
        store.domain.list.reset('ServiceProviderUserList');
        navigate(`/serviceproviders/edit/${serviceProviderId}/users`);
      },
    },
  ];

  return (
    <ServiceProviderLayout
      selectedStepperId="USERS"
      stackablePageButtons={getSPLevelStackableButtons({
        navigate,
      })}>
      <PageSection
        containerProps={{ sx: { mb: 3 } }}
        HeaderContent={
          <StackablePageButtons
            backButton={false}
            buttons={stackablePageButtons}
          />
        }
        label={formatMessage({
          id: 'CREATE_USER',
          defaultMessage: 'Create User',
        })}>
        <Box sx={{ mt: 2 }}>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permittedByDefault
            onSubmit={onSubmit}
            schema={schema}
            customComponents={{ ...FormFields, PasswordInstructions }}
            fetchDefaultValues={fetchDefaultValues}
            validationSchema={yupValidationSchema}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <PermissionButton
                permissionName="CAN_CREATE_SP_USER"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'SAVE_USER',
                  defaultMessage: 'Save User',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_SP_USER"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => {
                  formRef.current?.reset();
                }}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </Box>
          </Form>
        </Box>
      </PageSection>
    </ServiceProviderLayout>
  );
}

const UserCreateObservable = observer(UserCreate);
export default withServiceProvider(UserCreateObservable);
